/**
 * ADR-0246 + Suspicious v2 §4.2 — the addresses behind the "Unknown
 * scanner" row, from `GET /v1/sites/:site_id/scanner-ips`.
 *
 * The server decides who sees them (owner / admin / reviewer, the
 * visitor-IP rule of ADR-0243) and logs each reveal; other roles get
 * `status: 'hidden'` and a one-line note.
 *
 * Suspicious v2 adds a per-row Block button (owner / admin only; same
 * confirm-on-shared-forever semantics as everywhere else, routed
 * through `TurnOnBlocking`'s shared status-line helper). The old
 * "Block these addresses in your firewall…" sentence is replaced by
 * the status line.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import {
  deleteBlocklistEntry,
  getBlocking,
  getBlocklistLookup,
  postManualBlock,
  type BlockLookupHit,
} from '../../api/threats.js';
import type { HttpClient } from '../../api/http-client.js';
import type { BlockingState } from '@sill/types/api';
import { useC } from '../../lib/theme.js';
import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { fullPlaceLabel } from '../people/person-label.js';
import { BlockUnavailableButton, blockUnavailableReason, useIsShopifySite } from './BlockControl.js';
import { TurnOnBlockingLink } from './TurnOnBlocking.js';

export interface ScannerIpAddress {
  readonly address: string;
  readonly requests: number;
  readonly last_seen: string;
  readonly example_paths: readonly string[];
  /** Absent from an API that predates it. */
  readonly location?: { readonly city: string | null; readonly region: string | null; readonly country: string | null } | null;
}

export interface ScannerIpsResponse {
  readonly status: 'shown' | 'hidden';
  readonly addresses: readonly ScannerIpAddress[];
}

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Founder bug 2026-10-18: scanner-row chip copy, same shape as the
 * bot/agent per-row chip in BotAddressBlockSection.
 */
function scannerRowChipLabel(hit: BlockLookupHit | undefined, blocking: BlockingState | null): string {
  const notDelivering = blocking !== null
    && (blocking.mode === 'off' || blocking.delivery === 'failing' || blocking.install !== 'worker_ready');
  if (notDelivering) return 'Block saved · not active';
  if (hit === undefined) return 'Blocked';
  if (hit.expires_at === null) return 'Blocked forever';
  try {
    const d = new Date(hit.expires_at);
    if (Number.isNaN(d.getTime())) return 'Blocked';
    return `Blocked until ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d)}`;
  } catch { return 'Blocked'; }
}

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'loaded'; readonly data: ScannerIpsResponse };

export function ScannerIpsSection({ siteId, dateFrom, dateTo, onSearchAddress }: {
  readonly siteId: string;
  /** Clicking an address shows every visit it made (founder 2026-10-19). */
  readonly onSearchAddress?: (address: string) => void;
  readonly dateFrom?: string;
  readonly dateTo?: string;
}): JSX.Element | null {
  const C = useC();
  const isShopify = useIsShopifySite(siteId);
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const canMutate = auth !== null
    && auth.state.kind === 'authenticated'
    && (auth.state.user.role === 'owner' || auth.state.user.role === 'admin');
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [blocking, setBlocking] = useState<BlockingState | null>(null);
  const [blockedIps, setBlockedIps] = useState<ReadonlySet<string>>(() => new Set());
  // Founder bug 2026-10-18 (block-state consistency): per-row state
  // from the server-side blocklist/lookup so a scanner row that is
  // already blocked reads "Blocked until …" + Unblock, not "Block".
  const [addressBlocks, setAddressBlocks] = useState<ReadonlyMap<string, BlockLookupHit>>(() => new Map());
  const [unblockingIp, setUnblockingIp] = useState<string | null>(null);
  const [pendingIp, setPendingIp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (http === null) { setState({ kind: 'error' }); return; }
    setState({ kind: 'loading' });
    const qs = new URLSearchParams();
    if (dateFrom !== undefined && dateFrom.length > 0) qs.set('date_from', dateFrom);
    if (dateTo !== undefined && dateTo.length > 0) qs.set('date_to', dateTo);
    const q = qs.toString();
    let live = true;
    void http.request<ScannerIpsResponse>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(siteId)}/scanner-ips${q.length > 0 ? `?${q}` : ''}`,
    })
      .then((data) => { if (live) setState(data !== undefined && data !== null ? { kind: 'loaded', data } : { kind: 'error' }); })
      .catch(() => { if (live) setState({ kind: 'error' }); });
    return () => { live = false; };
  }, [http, siteId, dateFrom, dateTo]);

  useEffect(() => {
    if (http === null || !canMutate) return;
    void getBlocking(http, siteId)
      .then((r) => { if (r != null && r.blocking != null) setBlocking(r.blocking); })
      .catch(() => undefined);
  }, [http, siteId, canMutate]);

  const refreshAddressBlocks = useCallback(async () => {
    if (http === null || state.kind !== 'loaded' || state.data.status !== 'shown') return;
    const ips = state.data.addresses.map((a) => a.address).slice(0, 32);
    try {
      const { lookups } = await getBlocklistLookup(http, siteId, ips);
      const next = new Map<string, BlockLookupHit>();
      for (const l of lookups) if (l.blocked) next.set(l.ip, l);
      setAddressBlocks(next);
      setBlockedIps(new Set(next.keys()));
    } catch { /* best-effort */ }
  }, [http, siteId, state]);
  useEffect(() => { void refreshAddressBlocks(); }, [refreshAddressBlocks]);

  const onBlock = useCallback(async (ip: string) => {
    if (http === null) return;
    setPendingIp(ip);
    setError(null);
    try {
      const res = await postManualBlock(http, siteId, {
        target: { kind: 'ip', value: ip },
        duration: '7d',
        source: { kind: 'scanner', ref: 'agent_unknown_scanner' },
      });
      if (res.ok) {
        setBlockedIps((prev) => {
          const next = new Set(prev); next.add(ip); return next;
        });
        await refreshAddressBlocks();
      } else if (res.error === 'self_block_confirm_required') {
        // Retry with the confirm — scanner addresses are, by definition,
        // not the merchant's own, but we respect the server's gate: a
        // second click with confirm_self will land it.
        const retry = await postManualBlock(http, siteId, {
          target: { kind: 'ip', value: ip },
          duration: '7d',
          source: { kind: 'scanner', ref: 'agent_unknown_scanner' },
          confirm_self: true,
        });
        if (retry.ok) {
          setBlockedIps((prev) => { const next = new Set(prev); next.add(ip); return next; });
          await refreshAddressBlocks();
        } else {
          setError('That block was rejected.');
        }
      } else if (res.error === 'verified_crawler') {
        setError('That address overlaps a verified crawler and was not blocked.');
      } else if (res.error === 'cdn_or_proxy_range') {
        setError("That address belongs to Cloudflare or a privacy relay used by many real visitors, so Sill won't block it.");
      } else if (res.error === 'cloud_ranges_loading') {
        setError('Sill is still loading network data. Try again in a few seconds.');
      } else if (res.error === 'blocklist_cap_reached') {
        setError('Per-site blocklist cap reached. Remove an older block first.');
      } else {
        setError('That block was rejected.');
      }
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setPendingIp(null);
    }
  }, [http, siteId, refreshAddressBlocks]);

  const onUnblock = useCallback(async (ip: string) => {
    if (http === null) return;
    const hit = addressBlocks.get(ip);
    if (hit === undefined) return;
    setUnblockingIp(ip);
    setError(null);
    try {
      const r = await deleteBlocklistEntry(http, siteId, hit.block_id);
      if (!r.ok) setError('That unblock was rejected.');
      await refreshAddressBlocks();
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setUnblockingIp(null);
    }
  }, [http, siteId, addressBlocks, refreshAddressBlocks]);

  const kicker = useMemo(() => (
    <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>
      SCANNER ADDRESSES{(dateFrom ?? '').length === 0 && (dateTo ?? '').length === 0 ? ' · LAST 30 DAYS' : ''}
    </div>
  ), [C.muted, dateFrom, dateTo]);
  const note = (text: string): JSX.Element => (
    <div className="mb-4" data-testid="scanner-ips">
      {kicker}
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{text}</div>
    </div>
  );

  if (state.kind === 'loading') return note('Loading…');
  if (state.kind === 'error') return note("Addresses couldn't be loaded.");
  if (state.data.status === 'hidden') return note('Only owners, admins and reviewers can see scanner addresses.');
  if (state.data.addresses.length === 0) return note('No address was reported for these requests.');
  return (
    <div className="mb-4" data-testid="scanner-ips">
      {kicker}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {state.data.addresses.map((a) => {
          const already = blockedIps.has(a.address);
          const pending = pendingIp === a.address;
          return (
            <li
              key={a.address}
              data-testid="scanner-ip"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: `1px solid ${C.borderSoft}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 8, rowGap: 2 }}>
                  {/* Founder 2026-10-19: a flag on every address; unknown = muted block. */}
                  <span data-testid="scanner-ip-flag" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <CountryFlag code={a.location?.country ?? 'ZZ'} />
                  </span>
                  {onSearchAddress !== undefined ? (
                    <button
                      type="button"
                      data-testid="scanner-ip-search"
                      onClick={() => onSearchAddress(a.address)}
                      title={`Show every visit from ${a.address}`} /* title-ok: action hint on a bare address */
                      style={{
                        fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere',
                        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                        textDecoration: 'underline', textDecorationColor: C.border, textUnderlineOffset: 3, textAlign: 'left',
                      }}
                    >
                      {a.address}
                    </button>
                  ) : (
                    <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
                      {a.address}
                    </span>
                  )}
                  {(() => {
                    const place = fullPlaceLabel(a.location ?? null);
                    return place !== null ? (
                      <span data-testid="scanner-ip-location" style={{ fontFamily: sansFont, fontSize: 12, color: C.body }}>{place}</span>
                    ) : null;
                  })()}
                </div>
                <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {a.requests.toLocaleString('en-US')} request{a.requests === 1 ? '' : 's'}
                  {a.example_paths.length > 0 ? ` · e.g. ${a.example_paths.slice(0, 2).join(', ')}` : ''}
                </div>
              </div>
              {canMutate ? (
                already ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                    <span
                      data-testid="scanner-ip-blocked"
                      style={{
                        fontFamily: sansFont,
                        fontSize: 11,
                        padding: '3px 7px',
                        border: `1px solid ${C.coralFg}`,
                        color: C.coralFg,
                        borderRadius: 2,
                      }}
                    >
                      {scannerRowChipLabel(addressBlocks.get(a.address), blocking)}
                    </span>
                    <button
                      type="button"
                      data-testid="scanner-ip-unblock"
                      data-ip={a.address}
                      onClick={() => void onUnblock(a.address)}
                      disabled={unblockingIp === a.address}
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        padding: '4px 10px',
                        background: 'transparent',
                        color: C.text,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2,
                        cursor: unblockingIp === a.address ? 'wait' : 'pointer',
                        opacity: unblockingIp === a.address ? 0.6 : 1,
                      }}
                    >
                      {unblockingIp === a.address ? 'Unblocking…' : 'Unblock'}
                    </button>
                  </span>
                ) : blockUnavailableReason(blocking, { shopify: isShopify }) !== null ? (
                  <BlockUnavailableButton reason={blockUnavailableReason(blocking, { shopify: isShopify })!} testId="scanner-ip-block-disabled" />
                ) : (
                  <button
                    type="button"
                    data-testid="scanner-ip-block"
                    data-ip={a.address}
                    onClick={() => void onBlock(a.address)}
                    disabled={pending}
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      padding: '4px 10px',
                      background: 'transparent',
                      color: C.text,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      cursor: pending ? 'wait' : 'pointer',
                    }}
                  >
                    {pending ? 'Blocking…' : 'Block'}
                  </button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
      {error !== null ? (
        <div data-testid="scanner-ips-error" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 6 }}>
          {error}
        </div>
      ) : null}
      {canMutate && blocking !== null ? (
        <TurnOnBlockingLink
          siteId={siteId}
          siteDomain={null}
          http={http}
          blocking={blocking}
          liveBlocksCount={blockedIps.size}
          canMutate={canMutate}
          onModeFlipped={(install_override) => {
            setBlocking((prev) => prev === null ? prev : (install_override === null
              ? { ...prev, mode: 'on' }
              : { ...prev, install: install_override }));
          }}
        />
      ) : null}
    </div>
  );
}

// Suppress the HttpClient unused warning when the component tree
// trims the pathway without hooks (ts-strict pass).
export type _ScannerIpsHttp = HttpClient;
